import React, { useEffect, useState } from 'react';
import { Gauge, MapPin, Truck, Clock, AlertTriangle, Home } from 'lucide-react';
import { vozidloColor } from '../../utils/vozidloColor';
import { classifyVozidlo } from './VozidloDlazdice';

/**
 * Glassmorphism dlaždice řidiče pro Live tab Flotily — 1:1 port z POS Visual
 * Display (VozidloKarta). Vždy tmavé pozadí + frosted-glass blur, ať drží
 * vizuální shodu s 4K kuchyňským displejem; v light tématu OS to bude vypadat
 * jako přilepená tmavá kartička nad mapou (záměr, ne bug).
 *
 * Data shape:
 *   vozidlo:      { id, spz, model, rychlost_kmh, ignition, motion, smer, ... }
 *   ridic:        { jmeno, prijmeni, cele_jmeno } | null
 *   rozvozyEntry: { objednavky_na_ceste, objednavky_dorucene, eta_navrat_at,
 *                   rozvozce_jmeno } | null
 *   skore:        { znamka_abs, long_km, long_pocet_jizd, long_body_per_km,
 *                   km_celkem, pocet_jizd, body_per_km } | null
 */

// Inline barevný token + hex→rgba helper — bez shared lib, abychom nepřitáhli
// novou utility pro jedinou komponentu.
const COLORS = {
    brand:        '#FF8A00',
    textMuted:    '#4E4E5A',
    online:       '#34c759',
    danger:       '#ef4444',
    success:      '#10b981',
    textSecondary:'#8B8B9A',
};

function hexToRgba(hex, alpha = 1) {
    if (typeof hex !== 'string' || !hex.startsWith('#')) return hex;
    const h = hex.slice(1);
    const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(v.slice(0, 2), 16);
    const g = parseInt(v.slice(2, 4), 16);
    const b = parseInt(v.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Vehicle status — mimo semafor (green/orange), aby neclashovalo s KPI good/bad
// a ETA delta indicators. Violet = aktivní, slate = pasivní mezistav, gray = klid.
const STATUS_META = {
    driving: { color: '#8B5CF6', label: 'Jede' },
    idle:    { color: '#94A3B8', label: 'Stojí' },
    parked:  { color: '#6B7280', label: 'Parkováno' },
};

// Absolutní školní známka 1-5 (1 = výborný, 5 = nedostatečný). Backend ji
// počítá z 30-day body/km řidiče proti quintilům 90-day fleet dat (viz
// FlotilaScoringService::fleetPrahy).
const ZNAMKA_BARVY = {
    1: '#10B981', // emerald
    2: '#84CC16', // lime
    3: '#EAB308', // amber
    4: '#F97316', // orange
    5: '#EF4444', // red
};
const ZNAMKA_LABEL = {
    1: 'výborný', 2: 'chvalitebný', 3: 'dobrý', 4: 'dostatečný', 5: 'nedostatečný',
};

function buildZnamka(skore) {
    if (!skore) return null;
    const z = skore.znamka_abs;
    const km = skore.long_km ?? skore.km_celkem ?? 0;
    const jizd = skore.long_pocet_jizd ?? skore.pocet_jizd ?? 0;
    const bpkm = skore.long_body_per_km ?? skore.body_per_km;
    if (z == null) {
        return {
            color: '#6B7280',
            label: '—',
            tooltip: `Známka řidiče\nNedostatek dat za posledních 30 dní\nKm: ${km}  ·  Jízd: ${jizd}`,
        };
    }
    return {
        color: ZNAMKA_BARVY[z] || '#6B7280',
        label: String(z),
        tooltip: `Známka řidiče: ${z} (${ZNAMKA_LABEL[z]})\nPosledních 30 dní · Km: ${km}  ·  Jízd: ${jizd}\nBody/km: ${bpkm ?? '—'}`,
    };
}

function ridicDisplayName(rozvozyEntry, ridic) {
    if (rozvozyEntry?.rozvozce_jmeno) return rozvozyEntry.rozvozce_jmeno;
    if (ridic) return ridic.cele_jmeno || `${ridic.jmeno || ''} ${ridic.prijmeni || ''}`.trim();
    return null;
}

export default function VozidloKartaLive({ vozidlo, ridic, rozvozyEntry, skore, onClick, onOpenDetail }) {
    const status = classifyVozidlo(vozidlo);
    const meta = STATUS_META[status];
    const ring = vozidloColor(vozidlo.id);
    const ridicJmeno = ridicDisplayName(rozvozyEntry, ridic);
    const initials = (ridicJmeno || '?').split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
    const znamka = buildZnamka(skore);
    // Pořadí v kartě = pořadí, ve kterém řidič jede (poradi_plan z TrasaPlanService).
    const naCeste = [...(rozvozyEntry?.objednavky_na_ceste ?? [])]
        .sort((a, b) => (a.poradi_plan ?? 999) - (b.poradi_plan ?? 999));
    const dorucene = rozvozyEntry?.objednavky_dorucene ?? [];
    const speed = Math.max(0, Math.round(Number(vozidlo.rychlost_kmh || 0)));

    return (
        <div
            onClick={onClick}
            onDoubleClick={onOpenDetail}
            title="Dvojklik = otevřít detail"
            style={{
                // Glassmorphism: diagonální gradient s nízkou opacity, aby pod kartou
                // prosvítala mapa. Blur 20px + saturate(180%) = klasický frosted glass.
                background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderTopWidth: 1,
                borderRightWidth: 1,
                borderBottomWidth: 1,
                borderLeftWidth: 4,
                borderStyle: 'solid',
                borderTopColor: 'rgba(255,255,255,0.12)',
                borderRightColor: 'rgba(255,255,255,0.08)',
                borderBottomColor: 'rgba(255,255,255,0.06)',
                borderLeftColor: ring,
                borderRadius: 10,
                padding: 12,
                marginBottom: 8,
                cursor: 'pointer',
                // Inset highlight nahoře = lesk skla; venkovní stín + jemné svícení v barvě řidiče.
                boxShadow: `0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12), 0 0 24px ${hexToRgba(ring, 0.12)}`,
            }}
        >
            <div className="flex items-center gap-2.5 mb-2.5">
                <div
                    className="flex items-center justify-center flex-shrink-0 font-bold"
                    style={{
                        width: 44, height: 44, borderRadius: 9,
                        background: hexToRgba(ring, 0.22),
                        border: `2px solid ${hexToRgba(ring, 0.6)}`,
                        color: ring,
                        fontSize: 16,
                        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                    }}
                >
                    {initials}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                        <span
                            className="font-bold truncate"
                            style={{
                                color: '#FFFFFF',
                                fontSize: 17,
                                letterSpacing: -0.2,
                                textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                            }}
                        >
                            {ridicJmeno || '— bez řidiče —'}
                        </span>
                        {znamka && (
                            <span
                                className="inline-flex items-center justify-center font-bold flex-shrink-0"
                                style={{
                                    minWidth: 26, height: 22, borderRadius: 5,
                                    padding: '0 6px',
                                    background: hexToRgba(znamka.color, 0.28),
                                    border: `1px solid ${hexToRgba(znamka.color, 0.6)}`,
                                    color: znamka.color,
                                    fontSize: 13,
                                    letterSpacing: -0.2,
                                    fontVariantNumeric: 'tabular-nums',
                                    textShadow: `0 0 8px ${hexToRgba(znamka.color, 0.5)}`,
                                }}
                                title={znamka.tooltip}
                            >
                                {znamka.label}
                            </span>
                        )}
                    </div>
                    <div
                        className="flex items-center gap-1.5"
                        style={{
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: 12,
                            fontVariantNumeric: 'tabular-nums',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        }}
                    >
                        <span style={{ fontFamily: 'monospace', letterSpacing: 0.5, fontWeight: 600 }}>{vozidlo.spz}</span>
                        <span style={{ opacity: 0.5 }}>·</span>
                        <span>{vozidlo.model || ''}</span>
                    </div>
                </div>
                <span
                    className="font-bold px-2.5 py-1 rounded-full uppercase flex items-center gap-1.5 flex-shrink-0"
                    style={{
                        background: hexToRgba(meta.color, 0.22),
                        border: `1px solid ${hexToRgba(meta.color, 0.5)}`,
                        color: meta.color,
                        letterSpacing: 0.6,
                        fontSize: 11,
                        fontVariantNumeric: 'tabular-nums',
                        textShadow: `0 0 8px ${hexToRgba(meta.color, 0.4)}`,
                    }}
                >
                    <span style={{
                        width: 7, height: 7, borderRadius: '50%', background: meta.color,
                        boxShadow: `0 0 6px ${meta.color}`,
                        animation: status === 'driving' ? 'ms-pulse 1.5s ease-in-out infinite' : 'none',
                    }} />
                    {meta.label}
                </span>
            </div>

            <div className="flex items-center gap-4 mb-2">
                <div className="flex items-baseline gap-1.5">
                    <Gauge size={14} style={{ color: status === 'driving' ? COLORS.online : 'rgba(255,255,255,0.5)' }} />
                    <span
                        className="font-bold"
                        style={{
                            color: status === 'driving' ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
                            fontSize: 18,
                            letterSpacing: -0.3,
                            fontVariantNumeric: 'tabular-nums',
                            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        }}
                    >
                        {speed}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 500 }}>km/h</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                    <Truck size={14} style={{ color: COLORS.brand }} />
                    <span
                        className="font-bold"
                        style={{
                            color: '#FFFFFF',
                            fontSize: 18,
                            letterSpacing: -0.3,
                            fontVariantNumeric: 'tabular-nums',
                            textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        }}
                    >
                        {naCeste.length}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 500 }}>na cestě</span>
                </div>
                {dorucene.length > 0 && (
                    <div className="flex items-baseline gap-1.5">
                        <span style={{ color: COLORS.success, fontSize: 13, fontWeight: 700 }}>✓</span>
                        <span
                            className="font-bold"
                            style={{
                                color: COLORS.success,
                                fontSize: 18,
                                letterSpacing: -0.3,
                                fontVariantNumeric: 'tabular-nums',
                                textShadow: `0 0 8px ${hexToRgba(COLORS.success, 0.4)}`,
                            }}
                        >
                            {dorucene.length}
                        </span>
                    </div>
                )}
            </div>

            {naCeste.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    {naCeste.slice(0, 3).map((o) => (
                        <ObjednavkaTile key={o.id} objednavka={o} accent={ring} />
                    ))}
                    {naCeste.length > 3 && (
                        <div className="text-center" style={{ color: COLORS.textMuted, fontSize: 10 }}>
                            + {naCeste.length - 3} dalších
                        </div>
                    )}
                </div>
            )}

            {naCeste.length === 0 && dorucene.length > 0 && (
                <NavratNaBistro etaIso={rozvozyEntry?.eta_navrat_at} />
            )}
        </div>
    );
}

function NavratNaBistro({ etaIso }) {
    // Live tick — countdown se updatuje každou vteřinu, ne jen na poll (8s).
    // Hooky musí běžet vždy ve stejném pořadí, takže useState/useEffect
    // dáváme nahoru i pro variantu bez ETA (kdy interval zbytečně tiká,
    // ale render neovlivní).
    const [tick, setTick] = useState(Date.now());
    useEffect(() => {
        if (!etaIso) return;
        const id = setInterval(() => setTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, [etaIso]);

    if (!etaIso) {
        return (
            <div
                className="flex items-center gap-2 mt-1"
                style={{
                    background: `linear-gradient(135deg, ${hexToRgba(COLORS.brand, 0.18)} 0%, ${hexToRgba(COLORS.brand, 0.04)} 100%)`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderTopWidth: 1,
                    borderRightWidth: 1,
                    borderBottomWidth: 1,
                    borderLeftWidth: 3,
                    borderStyle: 'solid',
                    borderTopColor: hexToRgba(COLORS.brand, 0.28),
                    borderRightColor: hexToRgba(COLORS.brand, 0.18),
                    borderBottomColor: hexToRgba(COLORS.brand, 0.18),
                    borderLeftColor: COLORS.brand,
                    borderRadius: 7,
                    padding: '7px 11px',
                    color: COLORS.brand,
                    fontSize: 13,
                    fontWeight: 700,
                    textShadow: `0 0 8px ${hexToRgba(COLORS.brand, 0.4)}`,
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.08), 0 0 16px ${hexToRgba(COLORS.brand, 0.10)}`,
                }}
            >
                <Home size={14} />
                Vrací se na bistro
            </div>
        );
    }

    const eta = new Date(etaIso);
    const cas = eta.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
    const remainingMs = Math.max(0, eta.getTime() - tick);
    const mins = Math.floor(remainingMs / 60000);
    const secs = Math.floor((remainingMs % 60000) / 1000);
    const odpocet = mins > 0
        ? `${mins} min ${String(secs).padStart(2, '0')} s`
        : `${secs} s`;

    return (
        <div
            className="flex items-center justify-between mt-1"
            style={{
                background: `linear-gradient(135deg, ${hexToRgba(COLORS.brand, 0.18)} 0%, ${hexToRgba(COLORS.brand, 0.04)} 100%)`,
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                borderTopWidth: 1,
                borderRightWidth: 1,
                borderBottomWidth: 1,
                borderLeftWidth: 3,
                borderStyle: 'solid',
                borderTopColor: hexToRgba(COLORS.brand, 0.28),
                borderRightColor: hexToRgba(COLORS.brand, 0.18),
                borderBottomColor: hexToRgba(COLORS.brand, 0.18),
                borderLeftColor: COLORS.brand,
                borderRadius: 7,
                padding: '6px 10px',
                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.08), 0 0 16px ${hexToRgba(COLORS.brand, 0.10)}`,
            }}
        >
            <div
                className="flex items-center gap-2"
                style={{
                    color: COLORS.brand,
                    fontSize: 13,
                    fontWeight: 700,
                    textShadow: `0 0 8px ${hexToRgba(COLORS.brand, 0.4)}`,
                }}
            >
                <Home size={14} />
                Vrací se · {cas}
            </div>
            <div
                className="flex items-center gap-1"
                style={{
                    color: COLORS.brand,
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: -0.3,
                    fontVariantNumeric: 'tabular-nums',
                    textShadow: `0 0 10px ${hexToRgba(COLORS.brand, 0.5)}`,
                }}
            >
                {odpocet}
            </div>
        </div>
    );
}

function ObjednavkaTile({ objednavka, accent }) {
    const o = objednavka;
    const late = (o.lateness_min || 0) > 0;
    const eta = o.ocekavany_doraz_at
        ? new Date(o.ocekavany_doraz_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
        : null;

    // Delta ETA vs slíb (kladné = pozdě, záporné = brzy, 0 = přesně).
    let deltaMin = null;
    if (o.slibeny_at && o.ocekavany_doraz_at) {
        deltaMin = Math.round(
            (new Date(o.ocekavany_doraz_at).getTime() - new Date(o.slibeny_at).getTime()) / 60000
        );
    }
    const deltaColor = deltaMin == null
        ? null
        : deltaMin > 0 ? COLORS.danger
        : deltaMin < 0 ? COLORS.success
        : COLORS.textSecondary;
    const deltaText = deltaMin == null
        ? null
        : deltaMin === 0 ? 'včas'
        : deltaMin > 0 ? `+${deltaMin} min`
        : `${deltaMin} min`;

    return (
        <div
            style={{
                background: late
                    ? `linear-gradient(135deg, ${hexToRgba(COLORS.danger, 0.14)} 0%, ${hexToRgba(COLORS.danger, 0.04)} 100%)`
                    : 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: `1px solid ${late ? hexToRgba(COLORS.danger, 0.35) : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 7,
                padding: '6px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
            }}
        >
            <div
                className="flex items-center justify-center flex-shrink-0 font-bold"
                style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: late ? COLORS.danger : accent,
                    color: 'white',
                    letterSpacing: -0.3,
                    fontSize: 14,
                    fontVariantNumeric: 'tabular-nums',
                    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                    boxShadow: `0 2px 8px ${hexToRgba(late ? COLORS.danger : accent, 0.4)}`,
                }}
                title={`Denní č. ${o.cislo_denni ?? '?'}`}
            >
                {o.cislo_denni ?? '?'}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                    {o.poradi_plan != null && (
                        <span
                            className="font-bold uppercase px-1.5 py-0.5 rounded-full flex-shrink-0"
                            style={{
                                background: 'rgba(255,255,255,0.10)',
                                color: 'rgba(255,255,255,0.75)',
                                letterSpacing: 0.5,
                                fontSize: 10,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                            title="Pořadí na trase"
                        >
                            {o.poradi_plan}.
                        </span>
                    )}
                    <span
                        className="font-semibold truncate"
                        style={{
                            color: '#FFFFFF',
                            fontSize: 13,
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        }}
                    >
                        {o.zakaznik_jmeno || o.cislo_objednavky || `#${o.id}`}
                    </span>
                </div>
                <div
                    className="flex items-center gap-1"
                    style={{
                        color: 'rgba(255,255,255,0.70)',
                        fontSize: 11,
                        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                    }}
                >
                    <MapPin size={10} />
                    <span className="truncate">{o.adresa_doruceni || '—'}</span>
                </div>
            </div>
            {eta && (
                <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                    <div
                        className="flex items-center gap-1 font-semibold"
                        style={{
                            color: late ? COLORS.danger : 'rgba(255,255,255,0.85)',
                            fontSize: 12,
                            fontVariantNumeric: 'tabular-nums',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        }}
                    >
                        {late ? <AlertTriangle size={11} /> : <Clock size={11} />}
                        {eta}
                    </div>
                    {deltaText && (
                        <span
                            className="font-bold px-1.5 py-0.5 rounded"
                            style={{
                                background: hexToRgba(deltaColor, 0.20),
                                color: deltaColor,
                                lineHeight: 1,
                                fontSize: 10,
                                fontVariantNumeric: 'tabular-nums',
                                textShadow: `0 0 6px ${hexToRgba(deltaColor, 0.4)}`,
                            }}
                            title={o.slibeny_at ? `vs. slíb ${new Date(o.slibeny_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}` : undefined}
                        >
                            {deltaText}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
